import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal, Select, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

interface FormSettingsModalProps {
	isOpen: boolean
	onClose: () => void
	formId: string
}

const HIDE_RATER_NAMES_OPTIONS = [
	{ value: 'show', label: 'Show' },
	{ value: 'hide', label: 'Hide' },
]

export function FormSettingsModal({ isOpen, onClose, formId }: FormSettingsModalProps) {
	const toast = useToast()
	const queryClient = useQueryClient()

	const settingsQuery = useQuery({
		...trpc.admin.forms.getSettings.queryOptions({ id: formId }),
		enabled: isOpen,
	})

	const [hideRaterNames, setHideRaterNames] = useState<'show' | 'hide'>('show')

	useEffect(() => {
		if (settingsQuery.data) {
			const value = (settingsQuery.data as Record<string, unknown>).hideRaterNames
			setHideRaterNames(value === 'hide' ? 'hide' : 'show')
		}
	}, [settingsQuery.data])

	const updateMutation = useMutation({
		...trpc.admin.forms.updateSettings.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: trpc.admin.forms.getSettings.queryKey({ id: formId }) })
			toast.showToast('Form settings saved')
			onClose()
		},
		onError: () => {
			toast.showToast('Failed to save form settings', 'error')
		},
	})

	const handleSave = () => {
		updateMutation.mutate({ id: formId, settings: { hideRaterNames } })
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Form Settings'
			buttons={[
				{ variant: 'secondary', onClick: onClose, size: 'small', children: 'Cancel' },
				{
					variant: 'primary',
					onClick: handleSave,
					size: 'small',
					children: 'Save',
					disabled: updateMutation.isPending || settingsQuery.isLoading,
				},
			]}>
			<div className='flex flex-col gap-y-6 p-1'>
				<div className='flex flex-col gap-y-1.5'>
					<Text variant='small' className='font-medium text-neutral-700'>
						Hide Rater Names
					</Text>
					<Text variant='xsmall' className='text-neutral-500'>
						Controls whether evaluator names are shown in reports.
					</Text>
					<Select
						value={hideRaterNames}
						onChange={(value: string) => setHideRaterNames(value as 'show' | 'hide')}
						options={HIDE_RATER_NAMES_OPTIONS}
						disabled={settingsQuery.isLoading}
					/>
				</div>
			</div>
		</Modal>
	)
}
